import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Requiredaction, User } from '../models/auth/user';
import { Profile } from '../features/auth/profile-management/models/Profile';
import { MatDialog } from '@angular/material/dialog';
import { AttendenceComponent } from '../features/auth/attendance/attendence.component';
import { ResUserLoginRedirectEnum } from '../models/auth/login';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  private networkStatus = new Subject<boolean>();

  menuLoaded = new BehaviorSubject<any>(false);
  generalRefreshDetails = new BehaviorSubject<boolean>(false);

  actionsPermissions = new BehaviorSubject<any>(null);
  updateActionsPermissions = new Subject();

  SignalRConnectionId = new BehaviorSubject<any>(null);

  public currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    public dialog: MatDialog,
  ) {
    const authLS = localStorage.getItem('authUser');
    const ls = authLS ? JSON.parse(authLS) as User : '';
    if (ls) {
      this.currentUserSubject.next(ls);
    }
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }
  public get currentActionsPermissions(): any {
    return this.actionsPermissions.value;
  }
  public get currentSignalRConnectionId(): any {
    return this.SignalRConnectionId.value;
  }

  login(data: any): any {
    return this.http.post<User>(
      `${environment.apiUrl}/Authenticate/RestaurantLogin`, data)
      .pipe(map(user => {
        this.currentUserSubject.next(user);
        if (user.owneRestaurant.some(b => b.attendancefeature)) {
          this.openAttendenceDialog();
        } else {
          this.saveUserSession();
        }
      }));
  }
  openAttendenceDialog() {
    const dialogRef = this.dialog.open(AttendenceComponent, {
      width: '400px',
      data: { action: Requiredaction.ClockIn }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveUserSession();
      }
    });
  }
  saveUserSession() {
    let user = this.currentUserSubject.value;
    let restaurantId = localStorage.getItem('restaurantId');
    if (!restaurantId && user?.owneRestaurant.length) {
      localStorage.setItem('restaurantId', String(user.owneRestaurant[0].id));
    }
    localStorage.setItem('authUser', JSON.stringify(user));
    this.handleLoginRedirct();
  }
  handleLoginRedirct() {
    let user = this.currentUserSubject.value;
    let baseRoute = '/admin';
    switch (user?.loginRedirectEnum) {
      case ResUserLoginRedirectEnum.Dashboard:
        baseRoute = '/admin/dashboard';
        break;
      case ResUserLoginRedirectEnum.ReservationsList:
        baseRoute = '/admin/reservations/list';
        break;
      case ResUserLoginRedirectEnum.ReservationsFloor:
        baseRoute = '/admin/reservations/floor';
        break;
      case ResUserLoginRedirectEnum.Orders:
        baseRoute = '/admin/orders';
        break;
      case ResUserLoginRedirectEnum.OrdersList:
        baseRoute = '/admin/orderList/takeaway-pickup';
        break;
      case ResUserLoginRedirectEnum.Takeaway:
        baseRoute = '/admin/orderList/takeaway-pickup/placeorder';
        break;
      case ResUserLoginRedirectEnum.Profile:
        baseRoute = '/admin/profile';
        break;
      default:
        break;
    }
    localStorage.setItem('baseRoute', baseRoute);
    this.router.navigate([baseRoute]);
  }
  logout() {
    this.clearUserData();
    this.router.navigate(['/login']);
  }
  clearUserData() {
    localStorage.removeItem('authUser');
    localStorage.removeItem('restaurantId');
    localStorage.removeItem('orders');
    localStorage.removeItem('baseRoute');
    if (localStorage.getItem('deviceId')) {
      this.RestaurantLogOut();
    }
    this.currentUserSubject.next(null);
  }
  SetUserAttandance(data: any) {
    return this.http.post(`${environment.apiUrl}/ResRestaurant/SetUserAttandanceInRestaurant`, data);
  }
  RestaurantLogOut() {
    let deviceId = localStorage.getItem('deviceId');
    this.http.post(`${environment.apiUrl}/Authenticate/RestaurantLogOut`, { deviceId }).subscribe();
  }

  getActionsPermissions(restId: string) {
    return this.http.get(`${environment.apiUrl}/Lookup/GetActionsPermissions?RestaurantId=${restId}`);
  }
  forgetPassword(email: string): any {
    return this.http.get(`${environment.apiUrl}/Authenticate/RestaurantForgetPassword?email=` + email);
  }
  VerifyResetToken(userId: string, token: string) {
    return this.http.post(`${environment.apiUrl}/Authenticate/VerifyResetToken`, { userId, token });
  }
  resetPassword(userId: string, token: string, newPassword: string, firstTime: boolean) {
    if (firstTime) {
      return this.http.post(`${environment.apiUrl}/Authenticate/FirstTimePassword`, { userId, token, newPassword });
    } else {
      return this.http.post(`${environment.apiUrl}/Authenticate/ResetPassword`, { userId, token, newPassword });
    }
  }
  ChangePassword(oldPassword: string, newPassword: string) {
    return this.http.post(`${environment.apiUrl}/Authenticate/ChangePassword`, { oldPassword, newPassword });
  }
  checkResetPasswordLink(userId: string, token: string) {
    return this.http.post(`${environment.apiUrl}/Authenticate/VerifyToken`, { userId, token });
  }
  ConfirmAccount(userId: string, token: string) {
    return this.http.post(`${environment.apiUrl}/Authenticate/ConfirmAccount`, { userId, token });
  }



  GetUserDetails() {
    const user = this.currentUserValue || new User();
    this.http.get(`${environment.apiUrl}/Account/UserProfile`).subscribe((data: any) => {
      user.email = data.userEmail;
      user.fullName = data.fullName;
      user.id = data.userId;
      user.profilePicture = data.userImage;
      localStorage.setItem('authUser', JSON.stringify(user));
      setTimeout(() => {
        location.reload();
      }, 100);
    }, error => {
      this.logout();
    });
  }
  RefeshUserToken() {
    return this.http.post<any>(
      `${environment.apiUrl}/Authenticate/refresh-token`, {})
      .pipe(map(returnUser => {
        const user = this.currentUserValue || new User();
        user.token = returnUser.token;
        this.currentUserSubject.next(returnUser);
        localStorage.setItem('authUser', JSON.stringify(user));
        location.reload();
      }));

  }
  checkUniqueEmail(email: string, isProfile = false) {
    if (isProfile) {
      return this.http.get(`${environment.apiUrl}/User/IsEmailExist?email=` + email + '&isProfile=' + isProfile);
    } else {
      return this.http.get(`${environment.apiUrl}/User/IsEmailExist?email=` + email);
    }
  }
  RegisterUser(obj: object): any {
    return this.http.post(`${environment.apiUrl}/User/RegisterUser`, obj);
  }
  GetRestaurantUsersList(RestaurantId: any) {
    return this.http.get(`${environment.apiUrl}/Account/GetRestaurantUsersList?RestaurantId=${RestaurantId}`);
  }
  GetUserProfileDetails() {
    return this.http.get<Profile>(`${environment.apiUrl}/Account/ResUserProfile`);
  }

  get isOnline$() {
    return this.networkStatus.pipe(filter(Boolean));
  }
}
